import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    
    // ===== CUSTOM: Inicializa menu mobile slide lateral =====
    this.#initMobileSlideMenu();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');
      setTimeout(() => {
        trapFocus(details);
      }, 0);
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    onAnimationEnd(details, () => {
      reset(details);
      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
        openDetails.forEach(reset);
      } else {
        setTimeout(() => {
          trapFocus(this.refs.details);
        }, 0);
      }
    });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }

  /**
   * ===== CUSTOM: Inicializa o menu mobile com slide lateral =====
   * Converte accordion em slide lateral para mobile
   */
  #initMobileSlideMenu() {
    // Apenas para mobile
    if (window.innerWidth >= 750) return;
    
    const menuDrawer = this.querySelector('.menu-drawer');
    if (!menuDrawer) return;
    
    // Aguarda um pouco para garantir que o DOM está pronto
    setTimeout(() => {
      // Seleciona todos os details do menu principal (primeiro nível)
      // Tenta várias seleções possíveis
      const mainMenuDetails = menuDrawer.querySelectorAll(
        '.menu-drawer__menu.has-submenu > .menu-drawer__list-item > details, ' +
        '.menu-drawer__menu.has-submenu > .menu-drawer__list-item > accordion-custom > details, ' +
        '.menu-drawer__menu-container'
      );
      
      console.log('Found details elements:', mainMenuDetails.length);
      
      mainMenuDetails.forEach(details => {
        const summary = details.querySelector('summary');
        const submenuContainer = details.querySelector('.menu-drawer__submenu');
        const childList = details.querySelector('.menu-drawer__menu--childlist');
        const submenu = submenuContainer || childList;
        
        console.log('Processing detail:', summary?.textContent, 'Has submenu:', !!submenu);
        
        if (!summary || !submenu) return;
        
        // Adiciona classe personalizada
        details.classList.add('mobile-slide-menu');
        
        // Cria header do submenu se não existir
        if (!submenu.querySelector('.custom-submenu-header')) {
          console.log('Creating header for:', summary.textContent);
          this.#createSubmenuHeader(submenu, summary, details);
        }
        
        // Força estilos de slide lateral quando abrir
        summary.addEventListener('click', (e) => {
          setTimeout(() => {
            if (details.hasAttribute('open')) {
              console.log('Opening submenu:', summary.textContent);
              submenu.style.position = 'fixed';
              submenu.style.top = '0';
              submenu.style.left = '0';
              submenu.style.width = '100%';
              submenu.style.height = '100vh';
              submenu.style.backgroundColor = '#fff';
              submenu.style.zIndex = '9999';
              submenu.style.transform = 'translateX(0)';
              submenu.style.visibility = 'visible';
              submenu.style.opacity = '1';
              
              // Força o header a aparecer
              const header = submenu.querySelector('.custom-submenu-header');
              if (header) {
                header.style.display = 'flex';
              }
            }
          }, 50);
        });
      });
    }, 100);
  }

  /**
   * ===== CUSTOM: Cria o header do submenu com botões =====
   */
  #createSubmenuHeader(submenu, summary, details) {
    const header = document.createElement('div');
    header.className = 'custom-submenu-header';
    header.style.cssText = `
      display: flex !important;
      align-items: center !important;
      padding: 1rem 1.5rem !important;
      border-bottom: 1px solid #000!important;
      background-color: #fff !important;
      position: sticky !important;
      top: 0 !important;
      z-index: 10 !important;
      width: 100% !important;
      box-sizing: border-box !important;
    `;
    
    // Botão voltar
    const backBtn = document.createElement('button');
    backBtn.className = 'custom-back-button';
    backBtn.innerHTML = '‹';
    backBtn.setAttribute('type', 'button');
    backBtn.setAttribute('aria-label', 'Voltar');
    backBtn.style.cssText = `
      background: none !important;
      border: none !important;
      font-size: 2rem !important;
      line-height: 1 !important;
      cursor: pointer !important;
      padding: 0 !important;
      margin-right: 1rem !important;
      color: #000 !important;
      min-width: 44px !important;
      min-height: 44px !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
    `;
    
    // Título
    const title = document.createElement('span');
    const titleText = summary.querySelector('.menu-drawer__menu-item-text')?.textContent || summary.textContent;
    title.textContent = titleText.trim();
    title.style.cssText = `
      font-size: 1.125rem !important;
      font-weight: 500 !important;
      color: #000 !important;
      flex: 1 !important;
    `;
    
    // Botão fechar
    const closeBtn = document.createElement('button');
    closeBtn.className = 'custom-close-button';
    closeBtn.innerHTML = '×';
    closeBtn.setAttribute('type', 'button');
    closeBtn.setAttribute('aria-label', 'Fechar menu');
    closeBtn.style.cssText = `
      background: none !important;
      border: none !important;
      font-size: 2.5rem !important;
      line-height: 1 !important;
      cursor: pointer !important;
      padding: 0 !important;
      color: #000 !important;
      min-width: 44px !important;
      min-height: 44px !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
    `;
    
    header.appendChild(backBtn);
    header.appendChild(title);
    header.appendChild(closeBtn);
    
    // Insere no início do submenu
    submenu.insertBefore(header, submenu.firstChild);
    
    console.log('Header created and inserted for:', titleText);
    
    // Event listener para voltar
    backBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      console.log('Back button clicked');
      details.removeAttribute('open');
      summary.setAttribute('aria-expanded', 'false');
      details.classList.remove('menu-open');
    });
    
    // Event listener para fechar tudo
    closeBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      console.log('Close button clicked');
      this.close();
    });
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}

/**
 * ADICIONE ESTE CÓDIGO NO ARQUIVO: assets/header-drawer.js
 * 
 * Este código intercepta o comportamento do drawer e substitui
 * os accordions por painéis deslizantes no mobile
 */

// Aguardar o componente carregar
customElements.whenDefined('header-drawer').then(() => {
  
  class SlidingPanelMenu {
    constructor() {
      this.isInitialized = false;
      this.currentPanel = null;
      this.init();
    }

    init() {
      // Só executar em mobile
      if (window.innerWidth > 749) return;
      
      console.log('🚀 Inicializando menu com painéis deslizantes...');
      
      // Aguardar o drawer abrir
      const drawer = document.querySelector('header-drawer');
      if (!drawer) {
        console.error('❌ Header drawer não encontrado');
        return;
      }

      const details = drawer.querySelector('#Details-menu-drawer-container');
      if (!details) {
        console.error('❌ Details do menu não encontrado');
        return;
      }

      // Observer para detectar quando o drawer abre
      const observer = new MutationObserver(() => {
        if (details.hasAttribute('open') && !this.isInitialized) {
          setTimeout(() => this.transformMenu(), 100);
        }
      });

      observer.observe(details, { attributes: true });
      
      // Se já estiver aberto, inicializar
      if (details.hasAttribute('open')) {
        this.transformMenu();
      }
    }

    transformMenu() {
      console.log('🔧 Transformando menu...');
      
      const menuDrawer = document.querySelector('.menu-drawer');
      if (!menuDrawer || this.isInitialized) return;

      this.isInitialized = true;

      // Criar estrutura de painéis
      this.createPanelStructure();
      
      // Interceptar cliques nos itens com submenus
      this.interceptMenuClicks();
      
      // Adicionar estilos
      this.addStyles();
    }

    createPanelStructure() {
      const menuDrawer = document.querySelector('.menu-drawer');
      const mainMenu = menuDrawer.querySelector('.menu-drawer__menu.has-submenu');
      
      if (!mainMenu) return;

      // Criar container de painéis
      const panelContainer = document.createElement('div');
      panelContainer.className = 'sliding-panel-container';
      panelContainer.innerHTML = `
        <div class="sliding-panel-overlay"></div>
        <div class="sliding-panels-wrapper"></div>
      `;

      // Inserir após o menu drawer
      menuDrawer.parentElement.appendChild(panelContainer);

      // Processar cada item do menu principal
      const mainItems = mainMenu.querySelectorAll(':scope > li');
      mainItems.forEach((item, index) => {
        this.processMenuItem(item, index);
      });
    }

    processMenuItem(item, index) {
      const summary = item.querySelector('summary');
      const details = item.querySelector('details');
      
      if (!summary || !details) return;

      const title = summary.querySelector('.menu-drawer__menu-item-text')?.textContent.trim();
      const childMenu = details.querySelector('.menu-drawer__menu--childlist');
      
      if (!title || !childMenu) return;

      // Criar painel para este menu
      const panelId = `panel-${index}`;
      this.createPanel(panelId, title, childMenu);

      // Adicionar evento de clique
      summary.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.openPanel(panelId);
      });

      // Prevenir comportamento de accordion
      details.removeAttribute('open');
      details.addEventListener('toggle', (e) => {
        e.preventDefault();
        return false;
      });
    }

    createPanel(panelId, title, childMenu) {
      const wrapper = document.querySelector('.sliding-panels-wrapper');
      if (!wrapper) return;

      // Clonar o menu filho
      const menuClone = childMenu.cloneNode(true);
      
      // Criar painel
      const panel = document.createElement('div');
      panel.className = 'sliding-panel';
      panel.id = panelId;
      panel.innerHTML = `
        <div class="sliding-panel-header">
          <button class="sliding-panel-back" data-action="back">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M10 12L6 8L10 4" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </button>
          <div class="sliding-panel-title">${title}</div>
          <button class="sliding-panel-close" data-action="close">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </button>
        </div>
        <div class="sliding-panel-content"></div>
      `;

      // Adicionar menu clonado ao conteúdo
      const panelContent = panel.querySelector('.sliding-panel-content');
      panelContent.appendChild(menuClone);

      // Adicionar eventos aos botões
      panel.querySelector('[data-action="back"]').addEventListener('click', () => this.closePanel());
      panel.querySelector('[data-action="close"]').addEventListener('click', () => this.closeAllPanels());

      wrapper.appendChild(panel);
    }

    interceptMenuClicks() {
      // Prevenir todos os accordions de abrir
      const allDetails = document.querySelectorAll('.menu-drawer details');
      allDetails.forEach(details => {
        details.removeAttribute('open');
      });
    }

    openPanel(panelId) {
      console.log('📱 Abrindo painel:', panelId);
      
      const panel = document.getElementById(panelId);
      const overlay = document.querySelector('.sliding-panel-overlay');
      const container = document.querySelector('.sliding-panel-container');
      
      if (!panel || !overlay || !container) return;

      // Fechar todos os painéis primeiro
      document.querySelectorAll('.sliding-panel').forEach(p => {
        p.classList.remove('active');
      });

      // Abrir o painel selecionado
      panel.classList.add('active');
      overlay.classList.add('active');
      container.classList.add('active');
      document.body.style.overflow = 'hidden';
      
      this.currentPanel = panelId;

      // Adicionar evento ao overlay
      overlay.addEventListener('click', () => this.closePanel(), { once: true });
    }

    closePanel() {
      console.log('📱 Fechando painel');
      
      const overlay = document.querySelector('.sliding-panel-overlay');
      const container = document.querySelector('.sliding-panel-container');
      
      if (overlay) overlay.classList.remove('active');
      if (container) container.classList.remove('active');
      
      document.querySelectorAll('.sliding-panel').forEach(p => {
        p.classList.remove('active');
      });
      
      document.body.style.overflow = '';
      this.currentPanel = null;
    }

    closeAllPanels() {
      this.closePanel();
      
      // Fechar o drawer principal também
      const drawerDetails = document.querySelector('#Details-menu-drawer-container');
      if (drawerDetails) {
        drawerDetails.removeAttribute('open');
      }
    }

    addStyles() {
      if (document.getElementById('slidingPanelStyles')) return;

      const style = document.createElement('style');
      style.id = 'slidingPanelStyles';
      style.textContent = `
        @media screen and (max-width: 749px) {
          /* Esconder ícones de accordion */
          .menu-drawer .icon-plus,
          .menu-drawer .icon-caret {
            display: none !important;
          }

          /* Adicionar seta direita */
          .menu-drawer .has-submenu > li > details > summary::after {
            content: '→';
            margin-left: auto;
            font-size: 18px;
            color: #999;
            padding-right: 20px;
          }

          /* Prevenir accordion de abrir */
          .menu-drawer details[open] > .details-content {
            display: none !important;
          }

          /* Container dos painéis */
          .sliding-panel-container {
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            z-index: 9999;
            pointer-events: none;
          }

          .sliding-panel-container.active {
            pointer-events: auto;
          }

          /* Overlay */
          .sliding-panel-overlay {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0, 0, 0, 0.5);
            opacity: 0;
            visibility: hidden;
            transition: opacity 0.3s ease, visibility 0.3s ease;
          }

          .sliding-panel-overlay.active {
            opacity: 1;
            visibility: visible;
          }

   /* Wrapper dos painéis */
          .sliding-panels-wrapper {
            position: absolute;
            top: 0;
            left: 0;
            width: 95%;
            max-width: auto;
            height: 100%;
            pointer-events: none;
          }

          /* Painel individual */
          .sliding-panel {
            position: absolute;
            top: 0;
            left: -100%;
            width: 100%;
            height: 100%;
            background: #fff;
            display: flex;
            flex-direction: column;
            transition: left 0.3s ease;
            pointer-events: auto;
            box-shadow: 2px 0 10px rgba(0, 0, 0, 0.1);
          }-shadow: -2px 0 10px rgba(0, 0, 0, 0.1);
          }

          .sliding-panel.active {
            left: 0;
          }

          /* Header do painel */
          .sliding-panel-header {
            display: flex;
            align-items: center;
            padding: 15px 20px;
            border-bottom: 1px solid #000;
            min-height: 60px;
            background: #fff;
            flex-shrink: 0;
          }

          .sliding-panel-back,
          .sliding-panel-close {
            background: none;
            border: none;
            padding: 8px;
            cursor: pointer;
            color: #000;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .sliding-panel-back {
            margin-right: 10px;
          }

          .sliding-panel-close {
            margin-left: auto;
          }

          .menu-drawer__menu-item-text {
                      font-size: 16px;
            font-weight: 400;
            text-transform: lowercase;
          }

          .sliding-panel-title {
            flex: 1;
            text-align: center;
            font-size: 16px;
            font-weight: 400;
            text-transform: lowercase;
          }

          /* Conteúdo do painel */
          .sliding-panel-content {
            flex: 1;
            overflow-y: auto;
            -webkit-overflow-scrolling: touch;
          }

          /* Ajustar estilos do menu dentro do painel */
          .sliding-panel-content .menu-drawer__menu {
            /*padding-inline: 20px;*/
          }

          .sliding-panel-content .menu-drawer__menu-item {
            border-bottom: 1px solid #000;
            text-transform: lowercase;
          }

          .sliding-panel-content .menu-drawer__menu-item:hover {
            background: #f9f9f9;
          }

          /* Esconder accordions internos se houver */
          .sliding-panel-content details,
          .sliding-panel-content summary {
            display: none;
          }

        }
      `;
      
      document.head.appendChild(style);
    }
  }

  // Inicializar
  new SlidingPanelMenu();
  console.log('✅ Sistema de painéis deslizantes carregado!');
});

// Caso o customElements não seja suportado
if (!window.customElements) {
  console.warn('⚠️ CustomElements não suportado neste navegador');
}

